import { SORT_PARAMETER, SORT_DIRECTION } from '../constants/sortParameters.js'

// ── Tiebreaker level always appended last ─────────────────────────────────────
// Ensures deterministic order when all other sort keys are equal.
const ID_CAD_TIEBREAKER = {
  parameter: SORT_PARAMETER.ID_CAD,
  direction: SORT_DIRECTION.ASC,
  always_apply: true,
}

const DEFAULT_SORT_LEVELS = [
  { parameter: SORT_PARAMETER.STATUS, direction: SORT_DIRECTION.ASC, always_apply: false },
  { parameter: SORT_PARAMETER.OFFER,  direction: SORT_DIRECTION.ASC, always_apply: false },
]

function normalizeSortDirection(direction) {
  return direction === SORT_DIRECTION.DESC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
}

function normalizeSortLevel(level) {
  return {
    parameter: level?.sorting_parameter?.key ?? null,
    direction: normalizeSortDirection(level?.sorting_parameter?.direction),
    always_apply: level?.always_apply ?? false,
  }
}

/**
 * Returns the first UI-exposed user-editable sort level from a SortLevel[] chain.
 *
 * CMS may provide multiple `always_apply: false` levels for the initial catalog
 * order. Before the first user interaction they all stay active in CMS order,
 * but the UI exposes only the first editable level. Once the user changes sort,
 * the runtime rebuilds the chain to [user level, ...always_apply, id_cad].
 *
 * @param {SortLevel[] | null | undefined} sortConfig
 * @returns {SortLevel | null}
 */
export function getUserEditableSortLevel(sortConfig) {
  return sortConfig?.find((level) => !level.always_apply) ?? null
}

/**
 * Rebuilds the active sort chain after a user sort change.
 *
 * Result shape:
 *   [user-selected primary sort, ...always_apply CMS levels, id_cad tiebreaker]
 *
 * @param {SortLevel[] | null | undefined} sortConfig
 * @param {string | null | undefined} parameter
 * @param {'asc' | 'desc' | string | null | undefined} direction
 * @returns {SortLevel[]}
 */
export function applyUserSortSelection(sortConfig, parameter, direction) {
  const alwaysApplyLevels = (sortConfig ?? []).filter(
    (level) => level.always_apply && level.parameter !== SORT_PARAMETER.ID_CAD,
  )

  return [
    {
      parameter: parameter ?? null,
      direction: normalizeSortDirection(direction),
      always_apply: false,
    },
    ...alwaysApplyLevels,
    ID_CAD_TIEBREAKER,
  ]
}

/**
 * Compares two SortLevel[] chains for semantic equality.
 *
 * @param {SortLevel[] | null | undefined} a
 * @param {SortLevel[] | null | undefined} b
 * @returns {boolean}
 */
export function areSortConfigsEqual(a, b) {
  if (a === b) return true
  if (!Array.isArray(a) || !Array.isArray(b)) return false
  if (a.length !== b.length) return false

  return a.every((level, index) => {
    const other = b[index]
    return (
      level?.parameter === other?.parameter &&
      level?.direction === other?.direction &&
      level?.always_apply === other?.always_apply
    )
  })
}

/**
 * Builds the internal sort configuration (SortLevel[]) from panelSettings.
 *
 * The panel CMS emits each sort level as:
 *   { sorting_parameter: { key: string, direction: 'asc'|'desc' }, always_apply: boolean }
 *
 * This function normalizes that shape to the flat internal SortLevel:
 *   { parameter: string|null, direction: 'asc'|'desc', always_apply: boolean }
 *
 * An `id_cad` tiebreaker level is always appended at the end to ensure
 * deterministic ordering when all other sort keys are equal.
 *
 * - always_apply: true  → this levels always sorts AFTER users sort choice,
 * - always_apply: false → these levels fully participate in the initial CMS sort order;
 *                         after the first user sort change only the newly selected
 *                         primary level remains active and the rest are ignored.
 * - parameter: null     → inactive level, skipped by filterProducts
 *
 * @param {Object|null|undefined} initialSortingLevels
 * @returns {SortLevel[]}
 */
export function buildInitialSorting(initialSortingLevels) {
  const raw = initialSortingLevels

  let levels

  if (Array.isArray(raw) && raw.length > 0) {
    levels = raw.map(normalizeSortLevel)
  } else {
    // Sensible default: status → offer, both unlocked (always_apply: false).
    // The catalog UI still exposes only one user-editable slot; secondary levels
    // only matter until the user overrides sorting.
    levels = DEFAULT_SORT_LEVELS
  }

  // Always append id_cad as a final tiebreaker so identical-value rows are stable.
  return [...levels, ID_CAD_TIEBREAKER]
}